import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  Input,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { BookOutlined, ReloadOutlined, SearchOutlined, WarningOutlined } from '@ant-design/icons';
import {
  listModuleActions,
  listPermissionModules,
  listPermissionRoles,
} from '../dash/rbacApi';
import DashboardSection from '../dashboard/components/DashboardSection';
import AffectsCell from './AffectsCell';
import {
  ACTION_STATE_LABELS,
  buildCatalogRows,
  matchesCatalogQuery,
  uncataloguedPermissions,
} from './catalogViewHelpers';

const { Text, Paragraph } = Typography;

const STATE_COLOR = { live: 'green', inactive: 'orange', ungranted: 'default' };

/**
 * The capability catalog as a browsable screen: module -> actions -> the
 * permission behind each -> the roles that grant it -> the screens it opens.
 *
 * This chain existed in the data but had nowhere to stand. The catalog
 * surfaced only as two dropdowns in the Create Permission form and as grouping
 * inside the role editor, so "what can Facilities do, and who can do it" was
 * not a question the UI could answer in either direction.
 *
 * Deliberately read-only about roles. Nothing here grants anything — granting
 * happens in the role editor, one capability at a time. This is the map.
 */
export default function CatalogTab({ permissions = [] }) {
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [roleMap, setRoleMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roleMapError, setRoleMapError] = useState(null);
  const [q, setQ] = useState('');
  const query = q.trim();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRoleMapError(null);
    try {
      const [modulesBody, actionsBody] = await Promise.all([
        listPermissionModules(),
        listModuleActions('all', 'all'),
      ]);
      setModules(modulesBody?.data?.modules || []);
      setActions(actionsBody?.data?.actions || []);
    } catch (e) {
      // An empty catalog and an unreadable one look identical on screen, and
      // that is exactly how a broken catalog went unnoticed before.
      setError(e?.response?.data?.message || e?.message || 'Could not load the permission catalog');
      setModules([]);
      setActions([]);
    }

    try {
      const rolesBody = await listPermissionRoles();
      setRoleMap(rolesBody?.data?.permissions || {});
    } catch (e) {
      // Degrade rather than fail: the catalog is still worth showing without
      // the role column, but never pretend "no roles grant this".
      setRoleMap({});
      setRoleMapError(e?.response?.data?.message || e?.message || 'Could not load role assignments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(
    () => buildCatalogRows(modules, actions, roleMap),
    [modules, actions, roleMap],
  );

  const visibleRows = useMemo(() => {
    if (!query) return rows;
    return rows
      .map((row) => ({ ...row, actions: row.actions.filter((a) => matchesCatalogQuery(a, query)) }))
      .filter((row) => row.actions.length > 0);
  }, [rows, query]);

  const drift = useMemo(
    () => uncataloguedPermissions(permissions, actions),
    [permissions, actions],
  );

  const totals = useMemo(() => rows.reduce((acc, r) => ({
    actions: acc.actions + r.action_count,
    ungranted: acc.ungranted + r.ungranted_count,
    unheld: acc.unheld + r.unheld_count,
  }), { actions: 0, ungranted: 0, unheld: 0 }), [rows]);

  const moduleColumns = [
    {
      title: 'Module',
      dataIndex: 'label',
      key: 'label',
      render: (label, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{label}</Text>
          <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.module_key}</Text>
          {row.description ? (
            <Text type="secondary" style={{ fontSize: 12 }}>{row.description}</Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'action_count',
      key: 'action_count',
      width: 110,
      render: (count, row) => (
        <Tooltip title={`${row.granted_count} of ${count} have a permission`}>
          <Tag color={count ? 'blue' : 'default'}>{row.granted_count}/{count}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Roles',
      dataIndex: 'role_count',
      key: 'role_count',
      width: 90,
      render: (count) => <Tag color={count ? 'geekblue' : 'default'}>{count}</Tag>,
    },
    {
      title: 'Gaps',
      key: 'gaps',
      width: 220,
      render: (_, row) => (
        <Space size={4} wrap>
          {row.ungranted_count ? (
            <Tooltip title="Catalogued actions with no permission — nobody can be given these">
              <Tag>{row.ungranted_count} no permission</Tag>
            </Tooltip>
          ) : null}
          {row.unheld_count ? (
            <Tooltip title="Permissions that exist but no role grants — unreachable until assigned">
              <Tag color="orange">{row.unheld_count} unheld</Tag>
            </Tooltip>
          ) : null}
          {!row.ungranted_count && !row.unheld_count && row.action_count ? (
            <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
          ) : null}
        </Space>
      ),
    },
  ];

  const actionColumns = [
    {
      title: 'Action',
      dataIndex: 'label',
      key: 'label',
      render: (label, action) => (
        <Space direction="vertical" size={0}>
          <Text>{label}</Text>
          <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 12 }}>
            {action.permission_slug}
          </Text>
          {action.description ? (
            <Text type="secondary" style={{ fontSize: 12 }}>{action.description}</Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Permission',
      dataIndex: 'state',
      key: 'state',
      width: 160,
      render: (state) => <Tag color={STATE_COLOR[state]}>{ACTION_STATE_LABELS[state]}</Tag>,
    },
    {
      title: 'Granted to',
      key: 'roles',
      width: 260,
      render: (_, action) => {
        if (action.state === 'ungranted') {
          return <Text type="secondary" style={{ fontSize: 12 }}>No permission to grant</Text>;
        }
        if (!action.roles.length) {
          return (
            <Tooltip title="The permission exists but no role holds it, so nobody can do this yet.">
              <Tag color="orange" icon={<WarningOutlined />}>No role</Tag>
            </Tooltip>
          );
        }
        return (
          <Space size={4} wrap>
            {action.roles.map((role) => (
              <Tag key={role.id} color={role.is_active ? 'geekblue' : 'default'}>
                {role.display_name}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Opens',
      key: 'screens',
      width: 220,
      // Screens are a consequence of holding the permission — shown, never granted.
      render: (_, action) => <AffectsCell modules={action.screens} />,
    },
  ];

  return (
    <DashboardSection
      id="rbac-catalog"
      icon={<BookOutlined />}
      title="Capability catalog"
      subtitle="What each product area can do, which permission carries it, and who holds it"
      collapsible={false}
      variant="content"
      action={(
        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      )}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="Modules here are capabilities, not screens"
          description={
            <>
              A module groups the actions a product area can perform; a permission carries one
              action; a role is a set of permissions. Which <em>screens</em> that opens is shown in
              the last column and is derived, never granted — see the Screens tab for routes.
            </>
          }
        />

        {error ? (
          <Alert
            type="error"
            showIcon
            message="Could not load the catalog"
            description={<>{error}<br />Diagnostic: <Text code>php scripts/diagnose_rbac_permission_catalog.php</Text></>}
          />
        ) : null}

        {roleMapError ? (
          <Alert
            type="warning"
            showIcon
            message="Role assignments unavailable"
            description={`${roleMapError} — the "Granted to" column is blank because it could not be read, not because nothing is granted.`}
          />
        ) : null}

        {drift.length ? (
          <Alert
            type="warning"
            showIcon
            message={`${drift.length} permission${drift.length === 1 ? '' : 's'} outside the catalog`}
            description={
              <Space direction="vertical" size={4}>
                <Text>
                  These exist but no catalog action describes them, so they cannot be found by
                  browsing. Add a matching action from Permissions → New permission → New action.
                </Text>
                <Space size={4} wrap>
                  {drift.slice(0, 12).map((p) => (
                    <Tag key={p.id || p.name} style={{ fontFamily: 'monospace' }}>{p.name}</Tag>
                  ))}
                  {drift.length > 12 ? <Text type="secondary">+{drift.length - 12} more</Text> : null}
                </Space>
              </Space>
            }
          />
        ) : null}

        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search actions, slugs or the roles that hold them"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 380 }}
          />
          <Text type="secondary">
            {rows.length} modules · {totals.actions} actions
            {totals.ungranted ? ` · ${totals.ungranted} with no permission` : ''}
            {totals.unheld ? ` · ${totals.unheld} held by no role` : ''}
          </Text>
        </Space>

        {loading && !rows.length ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spin /></div>
        ) : visibleRows.length === 0 ? (
          <Empty description={query ? `Nothing matches "${query}"` : 'The catalog has no modules yet'} />
        ) : (
          <Table
            rowKey="key"
            dataSource={visibleRows}
            columns={moduleColumns}
            pagination={false}
            size="middle"
            expandable={{
              // Searching is only useful if what matched is already open.
              defaultExpandedRowKeys: query ? visibleRows.map((r) => r.key) : [],
              expandedRowRender: (row) => (
                row.actions.length ? (
                  <Table
                    rowKey="key"
                    dataSource={row.actions}
                    columns={actionColumns}
                    pagination={false}
                    size="small"
                  />
                ) : (
                  <Paragraph type="secondary" style={{ margin: 0 }}>
                    No actions yet. Add one from Permissions → New permission → New action in this module.
                  </Paragraph>
                )
              ),
            }}
          />
        )}
      </Space>
    </DashboardSection>
  );
}
